// Demo API mock — handles every backend call when demo mode is active.
//
// Path-based dispatch: the api.ts request() wrapper passes us the path
// (e.g. "/lessons", "/send-cover-notification") and we return the shape
// the caller expects. Any path that "sends an email" pushes an entry to
// the DemoInbox.
//
// Lessons/feedback/staff state lives in localStorage under the app's
// existing keys (cmc_app_state, cmc_onboarding_state) — the demo seed
// pre-populates them, and writes flow through the normal app code paths.
// So most fetches just return what's already there, plus a few synthetic
// endpoints (e.g. /lookup-schools-by-postcode) that need fake responses.

import { pushInboxEmail, pushToast, type DemoEmailKind } from './store';
import { answerFromSamplePolicy } from '@/lib/policy-sample';
import { DEMO_EMAILS, DEMO_LIMITS } from './config';
import type { Lesson, AppState, ClassEntity, PupilEntity } from '@/lib/app-store';
import { countArchivedEntries, curatePillarEntries, type PillarCurateField } from '@/lib/app-store';
import type { StaffMember } from '@/lib/onboarding-store';
import type { RetentionReviewStatus, RetentionReviewClass, RetentionRunResult } from '@/lib/api';

/** Demo mirror of the profile document's toursSeen list. */
const DEMO_TOURS_SEEN_KEY = 'cmc_demo_tours_seen_v1';
function readDemoToursSeen(): string[] {
  try {
    const raw = localStorage.getItem(DEMO_TOURS_SEEN_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
}


// Demo-only persistence for the Phase 1 entities. Survives reloads and is
// wiped on demo exit (cleared in store.ts:deactivateDemo).
const CLASSES_KEY = 'cmc_demo_classes_v1';
const PUPILS_KEY = 'cmc_demo_pupils_v1';

function readClasses(): ClassEntity[] {
  try {
    const raw = localStorage.getItem(CLASSES_KEY);
    return raw ? (JSON.parse(raw) as ClassEntity[]) : [];
  } catch {
    return [];
  }
}

function writeClasses(arr: ClassEntity[]) {
  try {
    localStorage.setItem(CLASSES_KEY, JSON.stringify(arr));
  } catch {
    // ignore
  }
}

function readPupils(): PupilEntity[] {
  try {
    const raw = localStorage.getItem(PUPILS_KEY);
    return raw ? (JSON.parse(raw) as PupilEntity[]) : [];
  } catch {
    return [];
  }
}

function writePupils(arr: PupilEntity[]) {
  try {
    localStorage.setItem(PUPILS_KEY, JSON.stringify(arr));
  } catch {
    // ignore
  }
}

function readAppState(): AppState | null {
  try {
    const raw = localStorage.getItem('cmc_app_state');
    return raw ? (JSON.parse(raw) as AppState) : null;
  } catch {
    return null;
  }
}

function writeAppState(state: AppState) {
  try {
    localStorage.setItem('cmc_app_state', JSON.stringify(state));
  } catch {
    // ignore
  }
}

// Pretty render a "this email would have been sent" body in plain text.
function renderBody(lines: string[]): string {
  return lines.filter(Boolean).join('\n');
}

function inboxEmail(
  kind: DemoEmailKind,
  toEmail: string,
  toName: string | undefined,
  subject: string,
  bodyLines: string[],
  options: {
    cta?: { label: string; url: string };
    meta?: Record<string, unknown>;
  } = {},
) {
  pushInboxEmail({
    kind,
    toEmail,
    toName,
    subject,
    body: renderBody(bodyLines),
    cta: options.cta,
    meta: options.meta,
  });
}

// Look up the demo staff member whose email matches `email`. Used so the
// "/invite-teacher" handler can build a /demo/teacher?id=<staffId> CTA
// that opens the correct teacher tab.
function findStaffByEmail(email: string): StaffMember | null {
  const app = readAppState();
  const norm = (email ?? '').trim().toLowerCase();
  return app?.school.staff?.find((s) => s.email.toLowerCase() === norm) ?? null;
}

// Current school admin email, taken from the seeded/edited onboarding
// state. Falls back to the canonical demo address.
function schoolAdminEmail(): string {
  try {
    const ob = JSON.parse(localStorage.getItem('cmc_onboarding_state') ?? '{}');
    const email = ob?.schoolData?.contactEmail as string | undefined;
    if (email && email.trim()) return email.trim();
  } catch {
    // ignore
  }
  return DEMO_EMAILS.schoolAdmin;
}

// Cover-assignment ledger persisted in localStorage so all demo tabs see
// the same picture. Mirrors what the real backend stores in Cosmos: which
// lesson is assigned to which cover-teacher email.
const ASSIGNMENTS_KEY = 'cmc_demo_cover_assignments_v1';

interface CoverAssignmentRecord {
  lessonId: string;
  coverEmail: string;
}

function readAssignments(): CoverAssignmentRecord[] {
  try {
    const raw = localStorage.getItem(ASSIGNMENTS_KEY);
    return raw ? (JSON.parse(raw) as CoverAssignmentRecord[]) : [];
  } catch {
    return [];
  }
}

function writeAssignments(arr: CoverAssignmentRecord[]) {
  try {
    localStorage.setItem(ASSIGNMENTS_KEY, JSON.stringify(arr));
  } catch {
    // ignore
  }
}

function recordCoverAssignment(lessonId: string, coverEmail: string) {
  if (!lessonId || !coverEmail) return;
  const norm = coverEmail.trim().toLowerCase();
  const next = readAssignments().filter((a) => a.lessonId !== lessonId);
  next.push({ lessonId, coverEmail: norm });
  writeAssignments(next);
}

// Email of the tab making the current request. Used by /cover-lessons to
// scope results to "what this teacher has been asked to cover". sessionStorage
// is per-tab so each browser tab gets its own scoped view.
function currentTabEmail(): string {
  try {
    return (sessionStorage.getItem('cmc_demo_tab_email') ?? '').trim().toLowerCase();
  } catch {
    return '';
  }
}

// The scripted sample policy and its three-question cap live in
// src/lib/policy-sample.ts, shared with the cover tour.

// Soft limit guard — returns true if the action should be blocked. In
// "soft warn" mode we never block, just toast.
function checkLimit(kind: 'lessons' | 'staff' | 'feedback' | 'secondaryContacts' | 'approvals', current: number): void {
  const max = DEMO_LIMITS[kind];
  if (current >= max) {
    pushToast('warn', `Demo limit: ${max} ${kind}. Oldest will be replaced if you add more.`);
  }
}

// ── Path dispatch ──────────────────────────────────────────────────────

interface RequestInfo {
  path: string;
  method: string;
  body: unknown;
}

function parseBody<T = Record<string, unknown>>(init?: RequestInit): T | null {
  if (!init?.body || typeof init.body !== 'string') return null;
  try {
    return JSON.parse(init.body) as T;
  } catch {
    return null;
  }
}

export async function demoRequest<T>(path: string, init?: RequestInit): Promise<T> {
  // Strip query string for matching, keep it for parsing
  const [bare, qs] = path.split('?');
  const method = (init?.method ?? 'GET').toUpperCase();
  const body = parseBody(init);
  const info: RequestInfo = { path: bare, method, body };

  // 50–200ms artificial latency so the UI's loading states are visible
  await new Promise((r) => setTimeout(r, 60 + Math.random() * 140));

  const result = await dispatch(info, qs ?? '');
  return result as T;
}

async function dispatch(info: RequestInfo, qs: string): Promise<unknown> {
  const { path, method, body } = info;
  const b = (body ?? {}) as Record<string, unknown>;

  // ── Email-sending endpoints ─────────────────────────────────────────
  // For each one we synthesize an inbox entry and return success.

  // ── Files (blob upload) — demo stub ────────────────────────────────
  // Demo mode never touches Azure Storage. We hand back a synthetic
  // blobPath and an `uploadUrl` that points at a special `demo://`
  // scheme; uploadFileToBlob recognises the scheme and stores the
  // file in localStorage instead of PUT-ing it anywhere. On read,
  // we return the same demo:// URL and the photo/file tiles render
  // the cached data URL. Same code paths exercise as production —
  // just with a different transport.

  if (path === '/files/upload-url' && method === 'POST') {
    const fileName = String(b.fileName ?? 'file');
    const stepId = String(b.stepId ?? 'step');
    const lessonId = String(b.lessonId ?? 'lesson');
    // Synthesize a deterministic-ish blobPath
    const blobPath = `demo/${lessonId}/${stepId}/${Date.now()}-${fileName}`;
    return { uploadUrl: `demo://${encodeURIComponent(blobPath)}`, blobPath, expiresAt: Date.now() + 15 * 60_000 };
  }
  if (path === '/files/read-url' && method === 'POST') {
    const blobPath = String(b.blobPath ?? '');
    const cached = localStorage.getItem(`cmc_demo_blob:${blobPath}`);
    if (!cached) {
      return { readUrl: '', expiresAt: 0 };
    }
    return { readUrl: cached, expiresAt: Date.now() + 6 * 60 * 60_000 };
  }

  // ── Summarise policies — demo stub ─────────────────────────────────
  if (path === '/summarise-policies' && method === 'POST') {
    return {
      rules: [
        'Follow the 3-step warning system: verbal warning, formal warning on the board, then removal to the partner classroom.',
        'Do not issue detentions directly, log incidents for the class teacher instead.',
        'One pupil out of the room at a time, with the corridor pass; they sign out on the board.',
        'Mobile phones stay off and in bags; confiscate to the front desk if used and log it.',
        'For anything involving physical danger, send a trusted pupil with the red card to the nearest staffed office, never leave the class unattended.',
      ],
    };
  }

  // ── Policy chat — demo stub ────────────────────────────────────────
  // Mirrors the production contract: grounded answers cite document +
  // page; anything the demo behaviour policy doesn't cover returns
  // grounded:false so the UI shows the honest contacts redirect.
  if (path === '/policy-chat' && method === 'POST') {
    // Scripted sample, capped at three per tab. Same code the cover tour uses.
    return answerFromSamplePolicy(String(b.question ?? ''), 'demo');
  }

  if (path === '/auth/send-email-otp' && method === 'POST') {
    const email = String(b.email ?? '');
    const code = String(Math.floor(100000 + Math.random() * 900000));
    inboxEmail(
      'school_otp',
      email,
      undefined,
      `Your CoverMyClass verification code: ${code}`,
      [
        `Hi,`,
        '',
        `Your verification code is: ${code}`,
        '',
        `It expires in 10 minutes. If you didn't request this, you can ignore this email.`,
      ],
      { meta: { code } },
    );
    // Store the code so verify-email-otp can validate it
    try {
      sessionStorage.setItem(`__demo_otp_${email}`, code);
    } catch {
      // ignore
    }
    return undefined;
  }

  if (path === '/auth/verify-email-otp' && method === 'POST') {
    const email = String(b.email ?? '');
    const code = String(b.code ?? '');
    let stored: string | null = null;
    try {
      stored = sessionStorage.getItem(`__demo_otp_${email}`);
    } catch {
      // ignore
    }
    // In demo, also accept "000000" as a master code so users don't have
    // to chase the inbox if they don't want to.
    const valid = code === stored || code === '000000';
    return { valid, ...(valid ? {} : { error: 'Invalid code, try 000000 or check the demo inbox.' }) };
  }

  if (path === '/auth/register-school' && method === 'POST') {
    inboxEmail(
      'school_invite',
      String(b.email ?? ''),
      String(b.schoolName ?? ''),
      'Welcome to CoverMyClass',
      [
        `Hi,`,
        '',
        `Your school account "${String(b.schoolName ?? 'Your School')}" is ready.`,
      ],
      { cta: { label: 'Open the admin dashboard', url: '/demo/school' } },
    );
    return { userId: 'demo-school-id' };
  }

  if (path === '/send-cover-notification' && method === 'POST') {
    const recipientEmail = String(b.toEmail ?? '');
    const staff = findStaffByEmail(recipientEmail);
    const teacherUrl = staff
      ? `/demo/teacher?id=${encodeURIComponent(staff.id)}`
      : '/demo/teacher';
    // Persist the assignment so /cover-lessons can scope the cover
    // dashboard to "lessons assigned to this tab's user".
    recordCoverAssignment(String(b.lessonId ?? ''), recipientEmail);
    const lines = [
      `Hi ${b.toName ?? 'there'},`,
      '',
      `You've been assigned to cover a lesson:`,
      `• Subject: ${b.subjectName ?? ''}`,
      `• Year: ${b.yearGroup ?? ''}`,
      `• Date: ${b.date ?? ''}`,
      `• Period: ${b.period ?? ''}`,
      `• Room: ${b.classroom ?? ''}`,
      `• Covering for: ${b.coveringFor ?? ''}`,
    ];
    const cta = { label: 'View your cover assignment', url: teacherUrl };
    inboxEmail(
      'cover_assignment',
      recipientEmail,
      String(b.toName ?? ''),
      `Cover lesson, ${b.subjectName ?? 'Lesson'} on ${b.date ?? ''}`,
      lines,
      { cta, meta: { lessonId: b.lessonId } },
    );
    return undefined;
  }

  if (path === '/send-bulk-cover-notification' && method === 'POST') {
    const lessons = (b.lessons as unknown[]) ?? [];
    const recipientEmail = String(b.toEmail ?? '');
    const staff = findStaffByEmail(recipientEmail);
    const teacherUrl = staff
      ? `/demo/teacher?id=${encodeURIComponent(staff.id)}`
      : '/demo/teacher';
    for (const l of lessons as Array<{ lessonId?: string }>) {
      recordCoverAssignment(String(l?.lessonId ?? ''), recipientEmail);
    }
    inboxEmail(
      'cover_bulk',
      recipientEmail,
      String(b.toName ?? ''),
      `Multiple cover lessons assigned (${lessons.length})`,
      [
        `Hi ${b.toName ?? 'there'},`,
        '',
        `You've been assigned ${lessons.length} cover lessons. View them in your dashboard.`,
      ],
      {
        cta: { label: 'Open your cover dashboard', url: teacherUrl },
        meta: { lessons },
      },
    );
    return undefined;
  }

  if (path === '/invite-teacher' && method === 'POST') {
    const teacherEmail = String(b.email ?? '');
    const teacherName = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
    const staff = findStaffByEmail(teacherEmail);
    const teacherUrl = staff
      ? `/demo/teacher?id=${encodeURIComponent(staff.id)}`
      : '/demo/teacher';
    const teacherSubject = `You've been invited to ${b.schoolName ?? 'CoverMyClass'}`;
    const teacherBody = [
      `Hi ${b.firstName ?? 'there'},`,
      '',
      `${b.schoolName ?? 'Your school'} has invited you to join CoverMyClass.`,
      'Click the button below to open your teacher dashboard.',
    ];
    const teacherCta = { label: `Open ${teacherName || 'teacher'}'s dashboard`, url: teacherUrl };

    // Email to the teacher (visible in that teacher's tab inbox).
    inboxEmail('teacher_invite', teacherEmail, teacherName, teacherSubject, teacherBody, {
      cta: teacherCta,
    });

    // Copy to the school admin so they can launch the teacher tab from
    // their own inbox after wizard completion. Subject is reworded to
    // make it obvious this is the admin's "sent" copy.
    const adminEmail = schoolAdminEmail();
    if (adminEmail && adminEmail.toLowerCase() !== teacherEmail.toLowerCase()) {
      inboxEmail(
        'teacher_invite',
        adminEmail,
        undefined,
        `Invite sent to ${teacherName || 'teacher'}`,
        [
          `You invited ${teacherName || teacherEmail} to ${b.schoolName ?? 'your school'}.`,
          '',
          'Click below to open their teacher dashboard in a new tab, useful for previewing the experience the teacher will see when they accept the invite.',
        ],
        { cta: teacherCta, meta: { sentTo: teacherEmail } },
      );
    }

    const app = readAppState();
    if (app) {
      const staffCount = (app.school.staff ?? []).length;
      checkLimit('staff', staffCount);
    }
    return undefined;
  }

  if (path === '/send-reset-link' && method === 'POST') {
    inboxEmail(
      'password_reset',
      String(b.email ?? ''),
      undefined,
      'Reset your CoverMyClass password',
      [
        `Hi,`,
        '',
        `Click the button below to reset your password.`,
        '',
        `This link expires in 1 hour.`,
      ],
      { cta: { label: 'Reset password', url: '/demo/school' } },
    );
    return undefined;
  }

  if (path === '/send-secondary-contact-invite' && method === 'POST') {
    const recipient = String(b.email ?? '');
    const recipientName = String(b.name ?? '');
    const subject = `You've been added as a contact for ${b.schoolName ?? 'a school'}`;
    const lines = [
      `Hi ${recipientName || 'there'},`,
      '',
      `${b.schoolName ?? 'A school'} has added you as a ${b.role ?? 'contact'} on CoverMyClass.`,
    ];
    const cta = { label: 'Activate your account', url: '/demo/school' };

    // To the contact themselves.
    inboxEmail('secondary_contact_invite', recipient, recipientName, subject, lines, { cta });

    // Sent-copy to the school admin so they can see the invite in their
    // own inbox alongside the teacher invites.
    const adminEmail = schoolAdminEmail();
    if (adminEmail && adminEmail.toLowerCase() !== recipient.toLowerCase()) {
      inboxEmail(
        'secondary_contact_invite',
        adminEmail,
        undefined,
        `Invite sent to ${recipientName || recipient}`,
        [
          `You invited ${recipientName || recipient} as a ${b.role ?? 'contact'}.`,
          '',
          'They\'ll see the invite in their own inbox once they open a demo tab.',
        ],
        { cta, meta: { sentTo: recipient } },
      );
    }
    return undefined;
  }

  if (path === '/notify-teacher' && method === 'POST') {
    inboxEmail(
      'teacher_termination',
      String(b.toEmail ?? ''),
      String(b.toName ?? ''),
      `Your CoverMyClass access has been removed`,
      [
        `Hi ${b.toName ?? 'there'},`,
        '',
        `Your access to ${b.schoolName ?? 'this school'} on CoverMyClass has been revoked.`,
        `If you think this is a mistake, contact your school admin.`,
      ],
    );
    return undefined;
  }

  // ── Lesson endpoints ────────────────────────────────────────────────

  if (path === '/lessons' && method === 'GET') {
    return readAppState()?.lessons ?? [];
  }

  if (path === '/lessons' && method === 'POST') {
    const app = readAppState();
    if (!app) return undefined;
    const lesson = b as Lesson;
    const existingIdx = app.lessons.findIndex((l) => l.id === lesson.id);
    let next = [...app.lessons];
    if (existingIdx >= 0) {
      next[existingIdx] = lesson;
    } else {
      checkLimit('lessons', next.length);
      // FIFO evict if at cap
      if (next.length >= DEMO_LIMITS.lessons) next = next.slice(1);
      next.push(lesson);
    }
    writeAppState({ ...app, lessons: next });
    return undefined;
  }

  if (path.startsWith('/lessons/') && method === 'PATCH') {
    const id = path.replace('/lessons/', '');
    const app = readAppState();
    if (!app) return undefined;
    const update = b as Partial<Lesson>;
    const next = app.lessons.map((l) => (l.id === id ? { ...l, ...update } : l));
    writeAppState({ ...app, lessons: next });
    // If this was an approval transition, send a fake email to the teacher
    if (update.status === 'approved' || update.status === 'rejected') {
      const lesson = next.find((l) => l.id === id);
      if (lesson?.teacherEmail) {
        const staff = findStaffByEmail(lesson.teacherEmail);
        const teacherUrl = staff
          ? `/demo/teacher?id=${encodeURIComponent(staff.id)}`
          : '/demo/teacher';
        inboxEmail(
          'other',
          lesson.teacherEmail,
          undefined,
          `Your lesson was ${update.status}`,
          [
            `Hi,`,
            '',
            `Your lesson on ${lesson.dateISO} (${lesson.subject}) has been ${update.status}.`,
            update.rejectionReason ? `Reason: ${update.rejectionReason}` : '',
          ],
          {
            cta: { label: 'Open your teacher dashboard', url: teacherUrl },
            meta: { lessonId: id, status: update.status },
          },
        );
      }
    }
    return undefined;
  }

  if (path.startsWith('/lessons/') && method === 'DELETE') {
    const id = path.replace('/lessons/', '');
    const app = readAppState();
    if (!app) return undefined;
    writeAppState({ ...app, lessons: app.lessons.filter((l) => l.id !== id) });
    return undefined;
  }

  if (path === '/cover-lessons' && method === 'GET') {
    // Cover teacher view — return only lessons the current tab's user
    // has actually been assigned to cover. Previously this returned
    // every awaiting_approval/approved lesson regardless of assignee,
    // so Priya could see lessons that had been given to Marcus.
    const me = currentTabEmail();
    if (!me) return [];
    const assigned = new Set(
      readAssignments()
        .filter((a) => a.coverEmail === me)
        .map((a) => a.lessonId),
    );
    const app = readAppState();
    return (app?.lessons ?? []).filter((l) => assigned.has(l.id));
  }

  if (path === '/lesson-feedback' && method === 'POST') {
    const app = readAppState();
    if (!app) return undefined;
    const fb = (b as { feedback: unknown }).feedback as AppState['feedback'][number];
    let next = [...app.feedback];
    checkLimit('feedback', next.length);
    if (next.length >= DEMO_LIMITS.feedback) next = next.slice(1);
    next.push(fb);
    writeAppState({ ...app, feedback: next });
    // Phase 2f — also stash any pillar suggestions in a demo-only
    // localStorage bucket so the review banner on the class profile
    // can show them. Real backend persists these as Cosmos docs;
    // here we just need them locally accessible.
    const suggestions = (b as { suggestions?: unknown[] }).suggestions;
    if (Array.isArray(suggestions) && suggestions.length > 0) {
      try {
        const raw = localStorage.getItem('cmc_demo_pillar_suggestions');
        const existing = raw ? (JSON.parse(raw) as unknown[]) : [];
        localStorage.setItem('cmc_demo_pillar_suggestions', JSON.stringify([...existing, ...suggestions]));
      } catch { /* ignore quota */ }
    }
    return undefined;
  }

  // Teacher asked for an archived observation back. The real backend emails the
  // school admin (functions/src/functions/retrievalRequestNotify.ts); without a
  // mock the demo swallowed it, so the teacher tour's final action produced
  // nothing admin-side and the grid badge looked seeded rather than earned.
  // Copy mirrors the real template, including the anti-phishing line, and
  // carries NO observation text — same as production.
  if (path === '/retrieval-request-notify' && method === 'POST') {
    const p = (b ?? {}) as { classLabel?: string; teacherName?: string; classId?: string };
    inboxEmail(
      'retrieval_request',
      schoolAdminEmail(),
      undefined,
      `Retrieval request: ${p.classLabel ?? 'a class'}`,
      [
        `${p.teacherName ?? 'A teacher'} has asked to restore an archived cover observation for ${p.classLabel ?? 'one of your classes'}.`,
        '',
        'Archived observations are kept for the record but no longer shown on the live class profile. Restoring one puts it back in front of the next cover teacher.',
        '',
        'Stay safe: CoverMyClass will never ask for your password by email.',
      ],
      // No CTA without a class id. An empty ?classId= reads as a real link and
      // does nothing when tapped — the exact silent-no-op this work removed
      // elsewhere. Better no button than a dead one.
      p.classId
        ? { cta: { label: 'Open the class profile', url: `/demo/school?classId=${p.classId}` } }
        : {},
    );
    return undefined;
  }

  if (path === '/pillar-suggestions' && method === 'GET') {
    const classId = qs ? new URLSearchParams(qs).get('classId') : null;
    if (!classId) return [];
    try {
      const raw = localStorage.getItem('cmc_demo_pillar_suggestions');
      const all = raw ? (JSON.parse(raw) as Array<{ classId: string; status: string }>) : [];
      return all.filter(s => s.classId === classId && s.status === 'pending');
    } catch {
      return [];
    }
  }

  if (path.startsWith('/pillar-suggestions/') && method === 'PATCH') {
    const id = path.split('/').pop()!;
    const status = (b as { status?: string }).status;
    if (status !== 'applied' && status !== 'discarded') return undefined;
    try {
      const raw = localStorage.getItem('cmc_demo_pillar_suggestions');
      const all = raw ? (JSON.parse(raw) as Array<{ id: string; status: string }>) : [];
      const next = all.map(s => s.id === id ? { ...s, status, resolvedAt: Date.now() } : s);
      localStorage.setItem('cmc_demo_pillar_suggestions', JSON.stringify(next));
    } catch { /* ignore */ }
    return undefined;
  }

  // ── School config ───────────────────────────────────────────────────

  if (path.startsWith('/school-config') && method === 'GET') {
    const app = readAppState();
    if (!app) return {};
    return mapSchoolToConfig(app);
  }

  if (path === '/school-config' && method === 'POST') {
    // No-op — the real app saves to localStorage separately via app-store helpers.
    return undefined;
  }

  // ── Staff status ────────────────────────────────────────────────────

  if (path.startsWith('/staff-status') && method === 'GET') {
    const app = readAppState();
    const activated = (app?.school.staff ?? [])
      .filter((s) => s.status === 'activated')
      .map((s) => ({ email: s.email, activatedAt: new Date(s.activatedAt ?? Date.now()).toISOString() }));
    return { activatedTeachers: activated };
  }

  // ── User profile ────────────────────────────────────────────────────
  // Mirrors the real profile document's toursSeen list.


  if (path === '/user-profile' && method === 'GET') {
    // Honor the same localStorage flags the rest of the app reads from
    // so users (and demo recordings) can pre-skip tutorials by writing
    // {completed: true, skipped: true} to those keys.
    const readSeen = (key: string): boolean => {
      try {
        const raw = localStorage.getItem(key);
        if (!raw) return false;
        const v = JSON.parse(raw) as { completed?: boolean; skipped?: boolean };
        return v.completed === true || v.skipped === true;
      } catch {
        return false;
      }
    };
    return {
      id: 'demo-user',
      tutorialSeen: readSeen('cmc_tutorial_state'),
      coverTutorialSeen: readSeen('cmc_cover_tutorial_state'),
      schoolTutorialSeen: readSeen('cmc_school_tutorial_state'),
      toursSeen: readDemoToursSeen(),
      firstActivatedAt: Date.now(),
      lastLoginAt: Date.now(),
    };
  }

  if (path === '/user-profile' && method === 'PATCH') {
    // Was a no-op, which quietly made demo the one place where account-level
    // tutorial memory did not exist — so demo would keep re-offering tours that
    // production had already retired. Demo is the same product, not a separate
    // one, so it persists the same thing (to localStorage rather than Cosmos).
    const key = typeof b.tourSeen === 'string' ? b.tourSeen : '';
    if (key) {
      try {
        const next = Array.from(new Set([...readDemoToursSeen(), key]));
        localStorage.setItem(DEMO_TOURS_SEEN_KEY, JSON.stringify(next));
      } catch { /* quota */ }
    }
    return undefined;
  }

  // ── Classes (Phase 1 of cover-continuity redesign) ──────────────────
  // Demo classes live in localStorage under cmc_demo_classes_v1 so they
  // persist across reloads + survive across the demo school + teacher
  // tabs. CRUD endpoints simulate the Cosmos-backed real flow.

  if (path === '/classes' && method === 'GET') {
    return readClasses();
  }

  if (path === '/classes' && method === 'POST') {
    const cls = b as Record<string, unknown> & { id?: string };
    if (!cls.id) return { error: 'Class must have an id' };
    const current = readClasses();
    const idx = current.findIndex((c) => c.id === cls.id);
    const next = idx >= 0 ? current.map((c, i) => (i === idx ? { ...c, ...cls } : c)) : [...current, cls];
    writeClasses(next as ReturnType<typeof readClasses>);
    return cls;
  }

  if (path.startsWith('/classes/') && method === 'DELETE') {
    const id = path.replace('/classes/', '');
    const next = readClasses().map((c) =>
      c.id === id ? { ...c, status: 'archived' as const, archivedAt: Date.now() } : c,
    );
    writeClasses(next);
    return undefined;
  }

  // ── Retention review (Stage 2 bulk review screen) ───────────────────
  // The real backend derives `cycleActive` from today's date against the
  // school's reviewMonth/reviewDay (default September 1) — reproducing that
  // here would make the demo's "grace period" story appear on only ~30 of
  // 365 days a year. Synthesized relative to Date.now() instead, so it's
  // always mid-grace-period whenever an admin actually looks.

  const RETENTION_FIELDS: PillarCurateField[] = [
    'knownMisconceptions', 'whatWorksWithThem', 'priorKnowledgeCanAlready', 'priorKnowledgeNotMetYet',
  ];

  function fieldEntries(cls: ClassEntity, field: PillarCurateField) {
    const p = cls.continuityPillars ?? {};
    switch (field) {
      case 'knownMisconceptions': return p.knownMisconceptions ?? [];
      case 'whatWorksWithThem': return p.whatWorksWithThem ?? [];
      case 'priorKnowledgeCanAlready': return p.priorKnowledge?.canAlready ?? [];
      case 'priorKnowledgeNotMetYet': return p.priorKnowledge?.notMetYet ?? [];
    }
  }

  function buildRetentionStatus(): RetentionReviewStatus {
    const classes = readClasses();
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const reviewDateISO = new Date(now - 20 * DAY).toISOString().slice(0, 10);
    const deletionDateISO = new Date(now + 10 * DAY).toISOString().slice(0, 10);
    const perClass: RetentionReviewClass[] = classes
      .map((c) => ({ classId: c.id, displayLabel: c.displayLabel, archivedCount: countArchivedEntries(c.continuityPillars) }))
      .filter((c) => c.archivedCount > 0);
    const totalArchived = perClass.reduce((n, c) => n + c.archivedCount, 0);
    return {
      policy: { enabled: true, reviewMonth: 9, reviewDay: 1, requireApproval: false, graceDays: 30 },
      totalArchived,
      classes: perClass,
      cycleActive: true,
      reviewDateISO,
      deletionDateISO,
      daysUntilDeletion: 10,
      deletedAt: null,
      deletionEnabled: true,
    };
  }

  if (path === '/retention-review' && method === 'GET') {
    return buildRetentionStatus();
  }

  if (path === '/retention-review/approve' && method === 'POST') {
    const classes = readClasses();
    const perClass: { classId: string; displayLabel: string; count: number }[] = [];
    let deleted = 0;
    const next = classes.map((cls) => {
      let pillars = cls.continuityPillars ?? {};
      let count = 0;
      for (const field of RETENTION_FIELDS) {
        const archivedIds = fieldEntries(cls, field).filter((e) => e.archivedAt).map((e) => e.id);
        if (archivedIds.length === 0) continue;
        count += archivedIds.length;
        pillars = curatePillarEntries(pillars, field, 'remove', archivedIds);
      }
      if (count > 0) {
        deleted += count;
        perClass.push({ classId: cls.id, displayLabel: cls.displayLabel, count });
        return { ...cls, continuityPillars: pillars, updatedAt: Date.now() };
      }
      return cls;
    });
    writeClasses(next);
    return { deleted, perClass };
  }

  if (path === '/retention-review/run' && method === 'POST') {
    const status = buildRetentionStatus();
    const result: RetentionRunResult = {
      applicable: status.totalArchived > 0,
      emailedOffset: null,
      deleted: status.totalArchived,
      dryRun: true,
    };
    return result;
  }

  // ── Pupils (Phase 1) ────────────────────────────────────────────────

  if (path === '/pupils' && method === 'GET') {
    return readPupils();
  }

  if (path === '/pupils' && method === 'POST') {
    const pupil = b as Record<string, unknown> & { id?: string };
    if (!pupil.id) return { error: 'Pupil must have an id' };
    const current = readPupils();
    const idx = current.findIndex((p) => p.id === pupil.id);
    const next = idx >= 0 ? current.map((p, i) => (i === idx ? { ...p, ...pupil } : p)) : [...current, pupil];
    writePupils(next as ReturnType<typeof readPupils>);
    return pupil;
  }

  if (path.startsWith('/pupils/') && method === 'DELETE') {
    const id = path.replace('/pupils/', '');
    const next = readPupils().map((p) =>
      p.id === id ? { ...p, status: 'archived' as const } : p,
    );
    writePupils(next);
    return undefined;
  }

  // ── Misc utilities ──────────────────────────────────────────────────

  if (path === '/push-config' && method === 'GET') {
    return { vapidPublicKey: 'demo-vapid-key' };
  }

  if (path === '/push-subscriptions' && method === 'POST') {
    return undefined;
  }

  if (path.startsWith('/lookup-schools-by-postcode')) {
    // Pass through to the real backend — this is a read-only public
    // school directory lookup (allschools.co.uk) with no email, auth, or
    // write side effects, so it's safe to call from demo. The user can
    // search the full UK school list, not just the seeded Bromley
    // sample. If the backend is unreachable (e.g. local dev with no
    // Azure Functions proxy), fall back to a tiny sample so the wizard
    // still demoes end-to-end.
    const fullPath = qs ? `${path}?${qs}` : path;
    try {
      const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api';
      const res = await fetch(`${apiBase}${fullPath}`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (res.ok) return res.json();
    } catch {
      // network error → fall through to sample list
    }
    const q = new URLSearchParams(qs).get('q')?.toLowerCase() ?? '';
    const samples = [
      { name: 'Bromley Demo Academy', postcode: 'BR2 9JE', town: 'Bromley', url: '#' },
      { name: 'Bromley High School', postcode: 'BR1 2TW', town: 'Bromley', url: '#' },
      { name: 'Bromhall Primary', postcode: 'BR3 1AA', town: 'Beckenham', url: '#' },
      { name: 'Brookside Secondary', postcode: 'SE20 7BJ', town: 'Penge', url: '#' },
    ];
    return {
      schools: samples.filter(
        (s) => s.name.toLowerCase().includes(q) || s.postcode.toLowerCase().includes(q),
      ),
    };
  }

  if (path === '/parse-timetable' && method === 'POST') {
    // Return a sensible parse so the wizard doesn't blow up if a user
    // tries the upload step.
    return {
      numberOfPeriods: 6,
      periodDuration: 60,
      schoolStartTime: '08:30',
      breakAfterPeriod: 2,
      lunchAfterPeriod: 4,
      periods: [
        { name: 'Period 1', startTime: '08:30', endTime: '09:30' },
        { name: 'Period 2', startTime: '09:30', endTime: '10:30' },
        { name: 'Period 3', startTime: '10:45', endTime: '11:45' },
        { name: 'Period 4', startTime: '11:45', endTime: '12:45' },
        { name: 'Period 5', startTime: '13:30', endTime: '14:30' },
        { name: 'Period 6', startTime: '14:30', endTime: '15:30' },
      ],
      breakTime: { startTime: '10:30', endTime: '10:45' },
      lunchTime: { startTime: '12:45', endTime: '13:30' },
      registration: null,
      subjects: ['English', 'Maths', 'Science'],
      rooms: ['M1', 'S1'],
    };
  }

  if (path === '/summarise-policies' && method === 'POST') {
    return {
      rules: [
        'Greet the class warmly and take a register.',
        'Refer disruptive behaviour to SLT on call.',
        'Leave the room as you found it.',
      ],
    };
  }

  // ── Emergency alerts ────────────────────────────────────────────────
  // These post to other devices in production; in demo they're local-only.

  if (path === '/emergency-alerts' && method === 'POST') {
    return undefined;
  }
  if (path.startsWith('/emergency-alerts/') && method === 'GET') {
    return { status: 'pending' };
  }
  if (path.includes('/accept') || path.includes('/cancel')) {
    return undefined;
  }

  // ── Retention review (Stage 2) ──────────────────────────────────────
  // Reports an OPEN annual cycle whenever the demo classes hold archived
  // observations, so the dashboard's amber banner + the bulk review screen
  // populate. Deletion is disabled in-demo (approve/run are no-ops) so the
  // page stays populated no matter what you click.
  if (path === '/retention-review' && method === 'GET') {
    const perClass = readClasses()
      .map((c) => ({ classId: c.id, displayLabel: c.displayLabel ?? 'Class', archivedCount: countArchivedEntries(c.continuityPillars) }))
      .filter((x) => x.archivedCount > 0)
      .sort((a, b) => b.archivedCount - a.archivedCount);
    const totalArchived = perClass.reduce((n, x) => n + x.archivedCount, 0);
    return {
      policy: { enabled: true, reviewMonth: 9, reviewDay: 1, requireApproval: false, graceDays: 30 },
      totalArchived,
      classes: perClass,
      cycleActive: totalArchived > 0,
      reviewDateISO: '2026-09-01',
      deletionDateISO: '2026-10-01',
      daysUntilDeletion: 30,
      deletedAt: null,
      deletionEnabled: true,
    };
  }
  if (path === '/retention-review/run' && method === 'POST') {
    return { applicable: true, emailedOffset: 0, deleted: 0, dryRun: true };
  }
  if (path === '/retention-review/approve' && method === 'POST') {
    return { deleted: 0, perClass: [] }; // demo never actually deletes
  }

  // Fallback — log and pretend success so the demo never breaks on an
  // unhandled endpoint.
  if (typeof console !== 'undefined') {
    console.warn(`[demo] unhandled ${method} ${path}, returning empty.`);
  }
  return undefined;
}

function mapSchoolToConfig(app: AppState) {
  const s = app.school;
  return {
    name: s.name,
    subjects: s.subjects,
    yearGroups: s.yearGroups,
    rooms: s.rooms,
    periods: s.periods,
    primaryColor: s.primary,
    adminEmail: s.contacts?.adminEmail ?? DEMO_EMAILS.schoolAdmin,
    postcode: s.postcode,
    address: s.address,
    urn: s.urn,
    dfeNumber: s.dfeNumber,
    logoImage: s.logoImage,
    departments: s.departments,
    policies: s.policies,
    contacts: s.contacts,
    secondaryContacts: s.contacts?.secondaryContacts,
    coverTeacherRules: s.coverTeacherRules,
    guestAccount: s.guestAccount,
    staff: s.staff,
    weekPattern: s.weekPattern,
    weekPeriods: s.weekPeriods,
    weekAnchorDate: s.weekAnchorDate,
  };
}
